import { useEffect, useState } from "react";
import { apiFetch } from "../lib/apiFetch";
import type { SupportLine } from "../types/api";

// One request per page load, shared by every caller. `settled` mirrors the
// resolved value so a later mount renders the number on its first paint.
let request: Promise<string | null> | null = null;
let settled: string | null | undefined;

function isSupportLine(body: unknown): body is SupportLine {
  return (
    typeof body === "object" &&
    body !== null &&
    "supportLineNumber" in body &&
    (typeof body.supportLineNumber === "string" || body.supportLineNumber === null)
  );
}

/**
 * Fetch the public support-line number at most once per page load.
 *
 * Later calls reuse the first request and its `baseUrl`. Resolves to the
 * E.164 number, or null when the server has none configured or the request
 * fails in any way (network, non-2xx, unexpected body). Never rejects.
 */
export function loadSupportLineNumber(baseUrl: string): Promise<string | null> {
  request ??= apiFetch<unknown>("/api/contact/support-line", {}, { baseUrl })
    .then((body) => (isSupportLine(body) && body.supportLineNumber ? body.supportLineNumber : null))
    .catch(() => null)
    .then((number) => {
      settled = number;
      return number;
    });
  return request;
}

/** Test seam: forget the cached request so each test fetches afresh. */
export function resetSupportLineNumberCache(): void {
  request = null;
  settled = undefined;
}

/**
 * The DearBill support-line number (E.164) for a `tel:` link.
 *
 * It is null while loading and whenever there is none; callers then render no
 * number and no label.
 */
export function useSupportLineNumber(baseUrl: string): string | null {
  const [number, setNumber] = useState<string | null>(() => settled ?? null);
  useEffect(() => {
    if (settled !== undefined) return;
    let cancelled = false;
    void loadSupportLineNumber(baseUrl).then((n) => {
      if (!cancelled) setNumber(n);
    });
    return () => {
      cancelled = true;
    };
  }, [baseUrl]);
  return number;
}
